import { use, useEffect, useEffectEvent } from 'react';

import type { ProviderEntry } from '#core/lib/expr/providers/useProviderRegistry.ts';
import { EvalDepsContext } from '#core/lib/expr/session/EvalDepsContext.ts';

/**
 * Register application dataset providers (the iPSO virtual views) into the
 * chat session's knowledge-graph group for the lifetime of the calling
 * component, ahead of the CDF view provider.
 *
 * Must be called from a component rendered INSIDE `<Chat>`: it reaches the
 * live registry through `EvalDepsContext` (`registerProvider` /
 * `unregisterProvider`), which `<ChatSessionProvider>` mounts. Rendering
 * inside the session is what lets the provider-building catalog hooks read
 * session context (the CDF client, and future per-session seams).
 *
 * Registration is idempotent by `provider.id` and removed on unmount.
 * Registration updates session state, so render-time consumers of the
 * registry (e.g. the DescribeViews view enum) re-render to reflect it. Pass a
 * stable `entries` reference (the React Compiler memoizes the caller's array);
 * the effect re-registers whenever that reference changes.
 */
export const useAppProvider = (entries: ProviderEntry[]): void => {
  const { registerProvider } = use(EvalDepsContext);

  const registerProviderEvent = useEffectEvent(registerProvider);

  useEffect(() => registerProviderEvent(entries), [entries]);
};
